// frontend/src/components/analytics/FinancialAnalytics.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  DollarSign, TrendingUp, TrendingDown, Clock, CheckCircle, XCircle,
  Calendar, Download, RefreshCw, AlertCircle, Wallet, PieChart
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const FinancialAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadData();
  }, [period]);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const params = { period };
      if (startDate && endDate) {
        params.start_date = startDate;
        params.end_date = endDate;
      }
      const response = await axios.get(`${API_BASE}/analytics/financial/dashboard`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      console.error('Error loading financial analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
        const token = localStorage.getItem('access_token');
        const start = startDate || data?.period?.start;
        const end = endDate || data?.period?.end;
        
        const response = await fetch(`${API_BASE}/analytics/financial/export?start_date=${start}&end_date=${end}&format=csv`, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
        });
        
        if (!response.ok) throw new Error('Export failed');
        
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `financial_analytics_${start}_${end}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Error exporting:', error);
        alert('Failed to export data');
    } finally {
        setExporting(false);
    }
    };

  const StatusBadge = ({ status }) => {
    const config = {
      paid: { label: 'Paid', color: 'bg-green-500/20 text-green-400' },
      pending: { label: 'Pending', color: 'bg-yellow-500/20 text-yellow-400' },
      overdue: { label: 'Overdue', color: 'bg-red-500/20 text-red-400' }
    };
    const c = config[status] || config.pending;
    return <span className={`px-2 py-1 rounded-full text-xs ${c.color}`}>{c.label}</span>;
  };

  const KpiCard = ({ title, value, subtext, icon: Icon, color }) => (
    <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
      <div className="flex items-center justify-between mb-2">
        <p className="text-gray-400 text-sm">{title}</p>
        <div className={`p-2 rounded-lg ${color}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
      {subtext && <p className="text-xs text-gray-500 mt-1">{subtext}</p>}
    </div>
  );

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-white">Financial Analytics</h2>
          <p className="text-gray-400 text-sm">Track revenue, invoices, and financial performance</p>
        </div>
        <div className="flex gap-2">
          <div className="flex bg-gray-700 rounded-lg p-0.5">
            {['week', 'month', 'year', 'all'].map((p) => (
              <button
                key={p}
                onClick={() => { setPeriod(p); setShowDatePicker(false); }}
                className={`px-3 py-1 text-sm rounded-md transition ${
                  period === p ? 'bg-blue-500 text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </button>
            ))}
            <button
              onClick={() => setShowDatePicker(!showDatePicker)}
              className={`px-3 py-1 text-sm rounded-md transition ${
                showDatePicker ? 'bg-blue-500 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              Custom
            </button>
          </div>
          <button onClick={loadData} className="p-2 hover:bg-gray-700 rounded-lg text-gray-400">
            <RefreshCw className="w-5 h-5" />
          </button>
          <button onClick={handleExport} className="p-2 hover:bg-gray-700 rounded-lg text-gray-400">
            <Download className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Custom Date Picker */}
      {showDatePicker && (
        <div className="flex gap-3 items-end p-4 bg-gray-800/50 rounded-xl border border-gray-700">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
            />
          </div>
          <button
            onClick={() => { loadData(); setShowDatePicker(false); }}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white"
          >
            Apply
          </button>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard
          title="Total Revenue"
          value={formatCurrency(data?.kpi?.total_revenue)}
          subtext={`Collection rate: ${data?.kpi?.collection_rate || 0}%`}
          icon={DollarSign}
          color="bg-green-500/20 text-green-400"
        />
        <KpiCard
          title="Pending"
          value={formatCurrency(data?.kpi?.pending_total)}
          subtext={`${data?.kpi?.pending_count || 0} invoices`}
          icon={Clock}
          color="bg-yellow-500/20 text-yellow-400"
        />
        <KpiCard
          title="Overdue"
          value={formatCurrency(data?.kpi?.overdue_total)}
          subtext={`${data?.kpi?.overdue_count || 0} invoices overdue`}
          icon={AlertCircle}
          color="bg-red-500/20 text-red-400"
        />
        <KpiCard
          title="Avg Payment"
          value={`${data?.payment_time?.avg_days_to_pay || 0} days`}
          subtext="to get paid"
          icon={TrendingUp}
          color="bg-blue-500/20 text-blue-400"
        />
      </div>

      {/* Monthly Revenue Chart */}
      <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
        <h3 className="text-white font-semibold mb-4">Monthly Revenue</h3>
        <div className="space-y-3">
          {data?.monthly_revenue?.slice(0, 6).map((month, idx) => {
            const maxRevenue = Math.max(...(data?.monthly_revenue?.map(m => m.revenue) || [1]));
            const percentage = (month.revenue / maxRevenue) * 100;
            return (
              <div key={idx} className="flex items-center gap-3">
                <span className="text-xs text-gray-500 w-24">
                  {new Date(month.month).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                </span>
                <div className="flex-1">
                  <div className="relative h-8 bg-gray-700 rounded-lg overflow-hidden">
                    <div 
                      className="absolute left-0 top-0 h-full bg-gradient-to-r from-green-500 to-green-400 rounded-lg transition-all duration-500"
                      style={{ width: `${percentage}%` }}
                    />
                    <div className="absolute left-2 top-1/2 -translate-y-1/2 text-white text-xs font-medium">
                      {formatCurrency(month.revenue)}
                    </div>
                  </div>
                </div>
                <span className="text-gray-400 text-sm w-20 text-right">{month.invoice_count} inv</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Status Distribution & Top Clients */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
          <h3 className="text-white font-semibold mb-4">Status Distribution</h3>
          <div className="space-y-3">
            {data?.status_distribution?.map((status, idx) => (
              <div key={idx}>
                <div className="flex items-center justify-between mb-1">
                  <StatusBadge status={status.status} />
                  <span className="text-white text-sm">{formatCurrency(status.amount)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-gray-700 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full bg-blue-500"
                      style={{ width: `${status.percentage}%` }}
                    />
                  </div>
                  <span className="text-gray-400 text-xs w-12">{status.percentage}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
          <h3 className="text-white font-semibold mb-4">Top Clients by Revenue</h3>
          <div className="space-y-3">
            {data?.top_clients?.slice(0, 5).map((client, idx) => {
              const maxAmount = data?.top_clients?.[0]?.total_amount || 1;
              const percentage = (client.total_amount / maxAmount) * 100;
              return (
                <div key={idx}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-white text-sm font-medium truncate">
                      {client.company_name || `${client.first_name} ${client.last_name}`}
                    </p>
                    <span className="text-green-400 text-sm">{formatCurrency(client.total_amount)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-700 rounded-full h-1.5">
                      <div 
                        className="h-1.5 rounded-full bg-green-500"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <span className="text-gray-400 text-xs">{client.invoice_count} inv</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Recent Invoices Table */}
      <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
        <div className="p-4 border-b border-gray-700">
          <h3 className="text-white font-semibold">Recent Invoices</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-800 border-b border-gray-700">
              <tr>
                <th className="p-3 text-left text-gray-400 text-sm">Invoice #</th>
                <th className="p-3 text-left text-gray-400 text-sm">Client</th>
                <th className="p-3 text-left text-gray-400 text-sm">Amount</th>
                <th className="p-3 text-left text-gray-400 text-sm">Status</th>
                <th className="p-3 text-left text-gray-400 text-sm">Due Date</th>
              </tr>
            </thead>
            <tbody>
              {data?.recent_invoices?.map((inv) => (
                <tr key={inv.id} className="border-b border-gray-700/50 hover:bg-gray-800/30">
                  <td className="p-3 text-white text-sm font-mono">{inv.invoice_number}</td>
                  <td className="p-3 text-gray-300 text-sm">
                    {inv.company_name || `${inv.first_name} ${inv.last_name}`}
                  </td>
                  <td className="p-3 text-white text-sm">{formatCurrency(inv.total_amount)}</td>
                  <td className="p-3"><StatusBadge status={inv.status} /></td>
                  <td className="p-3 text-gray-400 text-sm">
                    {inv.due_date ? new Date(inv.due_date).toLocaleDateString() : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FinancialAnalytics;